import {
    ConditionalRender,
    eventBus,
    isFailed,
    isFailedByOffline,
    isShowLoader,
    selectError,
    selectMutableWidget,
    useApp,
    useAppSelector,
    useAppStore,
    useNavigationBlockerViaHistory,
    useOnline,
    useSystemAction,
    useTranslation,
    useTriggerAction,
    useUiaRequestStats,
} from '@libs/core';
import { ActionSystemActionType, AssetsSvgMap, SystemEventTypeEnum, TCommonData, TWidget } from '@libs/types';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { CommonScreen } from './common/CommonScreen/CommonScreen';
import { buildOfflineToast, OFFLINE_MESSAGE_FALLBACK, OFFLINE_MESSAGE_KEY } from './common/Offline';
import OfflinePopup from './common/OfflinePopup';
import { TranslationKeys } from './types/enums';
import styled from './widgetWrapper.module.css';

const LOADER_DELAY = 800;
const EMPTY_WIDGET = {} as TWidget<TCommonData>;

export const WidgetWrapper = ({ children }: { children: React.ReactNode }) => {
    const isLoader = useAppSelector(isShowLoader);
    const isOnline = useOnline();
    const isStatusFlailed = useAppSelector(isFailed);
    const isOfflineFailure = useAppSelector(isFailedByOffline);
    const navigate = useNavigate();
    const { setPopup } = useApp();

    const { triggerAction } = useTriggerAction(EMPTY_WIDGET, true);
    const { t } = useTranslation();
    const systemActionNavigateBack = useSystemAction(ActionSystemActionType.navigateBack);
    useNavigationBlockerViaHistory(true, { triggerAction, systemActionNavigateBack });
    const uiaStats = useUiaRequestStats();
    const store = useAppStore();

    /**
     * "Try again" in the offline popup: replays the request that failed while offline.
     *
     * The failed action is read from the store at click time (not at render time), so the handler
     * captured inside the popup element can never replay a stale action. `actionForRepeat` /
     * `widgetUid` are filled in by the rejected `fetchScenario` / `fetchJsonAsync` thunks, which
     * only handle network actions — a Player action can never end up here.
     */
    const handleRetry = useCallback(() => {
        if (!navigator.onLine) {
            // Still no connection: keep the popup open and give feedback with the offline toast.
            eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, buildOfflineToast(t(OFFLINE_MESSAGE_KEY, OFFLINE_MESSAGE_FALLBACK)));
            return;
        }

        const state = store.getState();
        const error = selectError(state);
        setPopup(null);

        if (!error?.actionForRepeat) {
            // Nothing to replay (e.g. the initial scenario request failed) — restart the player.
            window.location.reload();
            return;
        }

        const widget = error.widgetUid ? selectMutableWidget(state, error.widgetUid) : undefined;
        triggerAction(error.actionForRepeat, {
            widgetAction: widget as TWidget<TCommonData> | undefined,
            isShowLoader: true,
            // Without the originating widget a widget-scoped request cannot be rebuilt, so replay it as a scenario one.
            forceScenario: !widget,
        }).catch((e) => console.error('Offline retry failed:', e));
    }, [setPopup, store, t, triggerAction]);

    useEffect(() => {
        if (isLoader) {
            // @ts-expect-error
            document?.activeElement?.blur?.();
        }
        if (!isOnline && isStatusFlailed) {
            setPopup(<OfflinePopup onRetry={handleRetry} />);
        }
    }, [handleRetry, isLoader, isOnline, isStatusFlailed, setPopup, uiaStats.isSlowConnection]);

    return (
        <>
            {/* A failure caused by a lost connection is handled by the offline flow (toast + popup), so the generic error screen stays hidden even once the network is back. */}
            <ConditionalRender condition={isOnline && isStatusFlailed && !isOfflineFailure}>
                <CommonScreen
                    useIcon={false}
                    fill="pink_100"
                    iconName={AssetsSvgMap.ic_x_circle_thin_80}
                    mainText={t(TranslationKeys.GeneralErrorText, 'Something went wrong...')}
                    buttonLabel={t(TranslationKeys.ButtonRetry, 'Try again')}
                    buttonHandleClick={() => {
                        navigate('/clearSession');
                    }}
                />
            </ConditionalRender>
            <ConditionalRender condition={isLoader}>
                <div className={styled.overlayContainer}>
                    <ConditionalRender condition={isLoader} delay={LOADER_DELAY}>
                        <CommonScreen useBackground={false} useSpinner />
                    </ConditionalRender>
                </div>
            </ConditionalRender>
            {children}
        </>
    );
};
